import { Injectable } from "@angular/core";
import { User } from "src/services/user/user.interface";
import { Role } from "src/services/user/role.enum";
import { BaseLocation } from "src/services/subscription/base-locations.enum";
import { ApiService } from "../http/api.service";
import { apiPath } from "../../shared/api-path.const";
import { fetchEveryPage, PageQuery } from "../http/pagination.utils";
import { Maybe } from "../utils/type.utils";
import {
  BackendBody,
  UserResponse,
  UsersDataPage,
  RolesResponse,
  UpdateUserRequest,
} from "../../shared/backend-api.types";

function toUser(response: UserResponse): User {
  return {
    id: response.userId,
    email: response.email,
    firstName: response.firstName,
    lastName: response.lastName,
    phone: response.phone ?? "",
    role: response.role as Role,
    howDidYouHearAboutUs: response.howDidYouHearAboutUs as User["howDidYouHearAboutUs"],
    tags: response.tags,
    createdAt: response.createdAt,
    hasActiveSubscription: response.hasActiveSubscription,
    pushToken: response.pushToken ?? undefined,
  };
}

@Injectable({
  providedIn: "root",
})
export class UserApiService {
  constructor(private readonly apiService: ApiService) {}

  public userWithEmailExists(email: string): Promise<BackendBody["ApiUserExists"]> {
    return this.apiService.sendGetRequest<BackendBody["ApiUserExists"]>(`${apiPath.user}/email/${email}`, undefined);
  }

  public getApiUserByExternalId(externalId: string): Promise<BackendBody["ApiUser"]> {
    return this.apiService.sendGetRequest<BackendBody["ApiUser"]>(
      `${apiPath.user}/by_external_id/${externalId}`,
      undefined,
    );
  }

  public async getByExternalId(externalId: string): Promise<User> {
    const response = await this.apiService.sendGetRequest<UserResponse>(
      `${apiPath.user}/by_external_id/${externalId}`,
      undefined,
    );
    return toUser(response);
  }

  public createNewUser(newUser: User & { baseLocationForDriver?: BaseLocation }): Promise<void> {
    return this.apiService.sendPostRequest<BackendBody["CreateUserArgs"], never>(
      apiPath.user,
      newUser as unknown as BackendBody["CreateUserArgs"],
    );
  }

  public signUp(profile: {
    firstName?: Maybe<string>;
    lastName?: Maybe<string>;
    phone?: Maybe<string>;
  }): Promise<void> {
    const body: { firstName?: string; lastName?: string; phone?: string } = {};
    if (profile.firstName) body.firstName = profile.firstName;
    if (profile.lastName) body.lastName = profile.lastName;
    if (profile.phone) body.phone = profile.phone;
    return this.apiService.sendPostRequest<typeof body, never>(`${apiPath.user}/signup`, body);
  }

  public async getById(id: string): Promise<User> {
    const response = await this.apiService.sendGetRequest<UserResponse>(`${apiPath.user}/${id}`, undefined);
    return toUser(response);
  }

  public async getAll(): Promise<User[]> {
    const response = await this.apiService.sendGetRequest<UsersDataPage>(apiPath.user, undefined);
    return response.users.map(toUser);
  }

  public async getAllCustomers(): Promise<User[]> {
    return fetchEveryPage(async (page) => {
      const response = await this.apiService.sendGetRequest<UsersDataPage, PageQuery & { role: string }>(apiPath.user, {
        ...page,
        role: "customer",
      });
      return { items: response.users.map(toUser), totalCount: response.totalCount };
    });
  }

  public async update(id: string, updates: Partial<User>): Promise<void> {
    const body: UpdateUserRequest = {};
    if (updates.firstName !== undefined) body.firstName = updates.firstName;
    if (updates.lastName !== undefined) body.lastName = updates.lastName;
    if (updates.phone !== undefined) body.phone = updates.phone;
    if (updates.pushToken !== undefined) body.pushToken = updates.pushToken;
    if (updates.role !== undefined) body.role = updates.role as UpdateUserRequest["role"];
    if (updates.tags !== undefined) body.tags = updates.tags;
    if (updates.howDidYouHearAboutUs !== undefined) body.howDidYouHearAboutUs = updates.howDidYouHearAboutUs;
    await this.apiService.sendPatchRequest<UpdateUserRequest, void>(`${apiPath.user}/${id}`, body);
  }

  public async softDelete(id: string): Promise<void> {
    await this.apiService.sendDeleteRequest<void>(`${apiPath.user}/${id}`, undefined);
  }

  public async getRoles(): Promise<string[]> {
    const response = await this.apiService.sendGetRequest<RolesResponse>(`${apiPath.user}/roles`, undefined);
    return response.roles;
  }

  public async search(emailOrName: string): Promise<User[]> {
    const response = await this.apiService.sendGetRequest<UsersDataPage, { search: string }>(apiPath.user, {
      search: emailOrName,
    });
    return response.users.map(toUser);
  }

  public async searchCustomers(emailOrName: string): Promise<User[]> {
    const response = await this.apiService.sendGetRequest<UsersDataPage, { role: string; search: string }>(
      apiPath.user,
      { search: emailOrName, role: "customer" },
    );
    return response.users.map(toUser);
  }

  public async searchStaff(emailOrName: string): Promise<User[]> {
    const response = await this.apiService.sendGetRequest<UsersDataPage, { role: string; search: string }>(
      apiPath.user,
      { search: emailOrName, role: "admin,driver" },
    );
    return response.users.map(toUser);
  }

  public async getByIds(ids: string[]): Promise<User[]> {
    if (ids.length === 0) return [];
    const response = await this.apiService.sendGetRequest<UsersDataPage, { ids: string }>(apiPath.user, {
      ids: ids.join(","),
    });
    return response.users.map(toUser);
  }
}
